import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  fetchContact,
  fetchInteractions,
  fetchPipelineStages,
  fetchCategories,
  updateContact,
  createInteraction,
} from '../api/crm';
import type { Contact, Interaction, PipelineStage, Category } from '../types/crm';
import { toDateOnly, formatDateDDMMYYYY } from '../utils/date';

export default function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const [contact, setContact] = useState<Contact | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newInteractionType, setNewInteractionType] = useState<'CALL' | 'EMAIL' | 'MEETING' | 'MESSAGE' | 'OTHER'>('CALL');
  const [newInteractionNotes, setNewInteractionNotes] = useState('');
  const [showLostReason, setShowLostReason] = useState(false);
  const [pendingStageId, setPendingStageId] = useState<string | null>(null);
  const [lostReason, setLostReason] = useState('');
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [nextFollowUp, setNextFollowUp] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const load = useCallback(() => {
    if (!contactId) return;
    setLoading(true);
    Promise.all([
      fetchContact(contactId),
      fetchInteractions(contactId),
      fetchPipelineStages(),
      fetchCategories(),
    ])
      .then(([c, i, s, cat]) => {
        setContact(c);
        setInteractions(i);
        setStages(s);
        setCategories(cat);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setJobTitle(contact?.job_title ?? '');
  }, [contact]);

  const handleStageChange = useCallback(
    async (stageId: string) => {
      if (!contact) return;
      const stage = stages.find((s) => s.id === stageId);
      if (stage?.requires_lost_reason) {
        setPendingStageId(stageId);
        setLostReason(contact.lost_reason ?? '');
        setShowLostReason(true);
        return;
      }
      if (stage?.requires_follow_up && !contact.next_follow_up_date) {
        setPendingStageId(stageId);
        setNextFollowUp(toDateOnly(contact.next_follow_up_date));
        setShowFollowUp(true);
        return;
      }
      try {
        await updateContact(contact.id, { ...contact, stage_id: stageId });
        setContact((prev) => (prev ? { ...prev, stage_id: stageId } : null));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update');
      }
    },
    [contact, stages]
  );

  const handleSubmitLostReason = useCallback(async () => {
    if (!contact || !lostReason.trim() || !pendingStageId) return;
    try {
      await updateContact(contact.id, { ...contact, stage_id: pendingStageId, lost_reason: lostReason.trim() });
      setShowLostReason(false);
      setPendingStageId(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }, [contact, lostReason, pendingStageId, load]);

  const handleAddInteraction = useCallback(async () => {
    if (!contactId) return;
    try {
      await createInteraction({ contact_id: contactId, type: newInteractionType, notes: newInteractionNotes.trim() || undefined });
      setNewInteractionNotes('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add interaction');
    }
  }, [contactId, newInteractionType, newInteractionNotes, load]);

  const category = contact?.category_id ? categories.find((c) => c.id === contact.category_id) : null;

  if (loading || !contact) {
    return <div>{loading ? 'Loading…' : 'Contact not found'}</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;
  }

  return (
    <>
      <nav style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
        <Link to="/crm" style={{ color: 'var(--color-blue-dark)' }}>CRM</Link>
        {' / '}
        <span>{contact.first_name} {contact.last_name}</span>
      </nav>
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 70%', minWidth: 0 }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 16px' }}>
            {contact.first_name} {contact.last_name}
          </h1>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600 }}>Stage</label>
            <select
              value={contact.stage_id}
              onChange={(e) => handleStageChange(e.target.value)}
              style={{ marginLeft: '8px', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
            >
              {stages.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>
            {contact.company && <div>Company: {contact.company}</div>}
            {contact.email && <div>Email: {contact.email}</div>}
            {contact.phone && <div>Phone: {contact.phone}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span>Job Title:</span>
              <input
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                onBlur={async () => {
                  const trimmed = jobTitle.trim();
                  if (trimmed === (contact.job_title ?? '')) return;
                  try {
                    await updateContact(contact.id, { job_title: trimmed || null });
                    setContact((prev) => prev ? { ...prev, job_title: trimmed || null } : null);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Failed to update');
                    setJobTitle(contact.job_title ?? '');
                  }
                }}
                placeholder="—"
                style={{ padding: '2px 6px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '14px', color: 'var(--color-text-secondary)' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span>Gender:</span>
              <select
                value={contact.gender ?? ''}
                onChange={async (e) => {
                  const val = e.target.value || null;
                  try {
                    await updateContact(contact.id, { gender: val });
                    setContact((prev) => prev ? { ...prev, gender: val } : null);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update');
                  }
                }}
                style={{ padding: '2px 6px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '14px', color: 'var(--color-text-secondary)' }}
              >
                <option value="">—</option>
                <option value="M">M</option>
                <option value="F">F</option>
                <option value="O">O</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span>Employment Status:</span>
              <select
                value={contact.employment_status ?? ''}
                onChange={async (e) => {
                  const val = e.target.value || null;
                  try {
                    await updateContact(contact.id, { employment_status: val });
                    setContact((prev) => prev ? { ...prev, employment_status: val } : null);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update');
                  }
                }}
                style={{ padding: '2px 6px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '14px', color: 'var(--color-text-secondary)' }}
              >
                <option value="">—</option>
                <option value="active">Active</option>
                <option value="changed">Changed</option>
              </select>
            </div>
            {category && <div>Category: {category.name}</div>}
            <div>Outreach: {contact.outreach_type}</div>
            {contact.next_follow_up_date && (
              <div style={{ color: new Date(contact.next_follow_up_date) < new Date() ? 'var(--color-coral)' : undefined }}>
                Next follow-up: {formatDateDDMMYYYY(contact.next_follow_up_date)}
              </div>
            )}
          </div>
          {contact.notes && (
            <div style={{ padding: '16px', background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '24px' }}>
              <strong>Notes</strong>
              <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '14px' }}>{contact.notes}</pre>
            </div>
          )}
        </div>
        <div style={{ width: '320px', flexShrink: 0 }}>
          <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-blue-dark)', marginBottom: '12px' }}>
            Timeline
          </h3>
          <div style={{ marginBottom: '12px' }}>
            <select
              value={newInteractionType}
              onChange={(e) => setNewInteractionType(e.target.value as Interaction['type'])}
              style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '8px' }}
            >
              <option value="CALL">Call</option>
              <option value="EMAIL">Email</option>
              <option value="MEETING">Meeting</option>
              <option value="MESSAGE">Message</option>
              <option value="OTHER">Other</option>
            </select>
            <textarea
              value={newInteractionNotes}
              onChange={(e) => setNewInteractionNotes(e.target.value)}
              placeholder="Notes…"
              rows={2}
              style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '8px', resize: 'vertical' }}
            />
            <button
              type="button"
              onClick={handleAddInteraction}
              style={{ padding: '8px 16px', background: 'var(--color-blue-dark)', color: 'white', border: 'none', borderRadius: 'var(--radius)', fontWeight: 500 }}
            >
              Add
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {interactions.map((i) => (
              <div
                key={i.id}
                style={{
                  padding: '12px',
                  background: 'white',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '14px',
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: '4px' }}>{i.type}</div>
                {i.notes && <p style={{ margin: '0 0 4px' }}>{i.notes}</p>}
                <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{new Date(i.date).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showLostReason && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowLostReason(false)} role="presentation">
          <div style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Lost reason required</h3>
            <textarea value={lostReason} onChange={(e) => setLostReason(e.target.value)} rows={3} style={{ width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={handleSubmitLostReason} style={{ padding: '10px 20px', background: 'var(--color-blue-dark)', color: 'white', border: 'none', borderRadius: 'var(--radius)' }}>Save</button>
              <button type="button" onClick={() => setShowLostReason(false)} style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showFollowUp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowFollowUp(false)} role="presentation">
          <div style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Next follow-up date required</h3>
            <input
              type="date"
              value={toDateOnly(nextFollowUp)}
              onChange={(e) => setNextFollowUp(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!contact || !nextFollowUp) return;
                  try {
                    await updateContact(contact.id, {
                      ...contact,
                      next_follow_up_date: nextFollowUp,
                      ...(pendingStageId ? { stage_id: pendingStageId } : {}),
                    });
                    setShowFollowUp(false);
                    setPendingStageId(null);
                    load();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Failed');
                  }
                }}
                style={{ padding: '10px 20px', background: 'var(--color-blue-dark)', color: 'white', border: 'none', borderRadius: 'var(--radius)' }}
              >
                Save
              </button>
              <button type="button" onClick={() => setShowFollowUp(false)} style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
